import { AddCustomer } from "api/customer/customer";
import { RESEND_AFTER_SECONDS } from "utils/trackBooking";

/**
 * The checkout's email code for a returning renter (1NOW-10770).
 *
 * It is the add-website-customer call again, not a new endpoint: with `email_code: true` a renter who has booked
 * with this company before gets no session but `{ code_required: true, detail, resend_after }` and a six-digit code
 * mailed to them; the same body with `code` answers with the full session. A new renter, and an email with no
 * booking here, get today's answer (a token), so they never see `code_required`.
 */

/** What AddCustomer was sent: the renter's details and the company. */
export interface CustomerBody {
  first_name: string;
  last_name: string;
  email: string;
  phone_no: string;
  company: number | string;
}

/** What the code box tells its host after a code was typed. `bad`: the code itself was refused (red boxes). */
export type VerifyOutcome = { ok: true } | { error: string; bad?: boolean };

/** One code question on screen: the Pay step, the dialog or the Reserved page asks it, the box answers it. */
export interface CodeGate {
  customer: CustomerBody;
  /** Seconds until another code may be asked for, as the backend said. */
  resendAfter?: number;
  /** Why no code works yet (asked again too soon), shown in the box from the start. */
  error?: string;
  /** Six digits were entered: check them and carry on. */
  onVerify: (code: string) => Promise<VerifyOutcome>;
  /** "Use a different email": the host takes the renter back to their details. No booking was made. */
  onDifferentEmail: () => void;
}

/** AddCustomer's answer is the code question (a returning renter's email): no session in it. */
export const codeRequired = (data: any): boolean => data?.code_required === true;

/** The code question from AddCustomer's answer, for a gate: the wait and any refusal. */
export const codeGateFrom = (customer: CustomerBody, data: any) => ({
  customer,
  resendAfter: Number(data?.resend_after) || undefined,
  error: data?.error,
});

/** The code was typed: the full session (access_token, refresh_token, id, ...) or the backend's own refusal. */
export const verifyEmailCode = async (customer: CustomerBody, code: string): Promise<{ data: any } | { error: string }> => {
  const r = await AddCustomer({ ...customer, email_code: true, code });
  if (r.error) return { error: r.error };
  // A right code is answered with the session, never with another request for a code.
  if (codeRequired(r.data)) return { error: r.data.error ?? "That code isn't right or has expired." };
  return { data: r.data };
};

/**
 * "Resend code" is the same call without `code`: a new mail once the wait is over, "already sent" while the last
 * code still works, the wait in words when it is too soon. The backend's own words are shown either way.
 */
export const resendEmailCode = async (
  customer: CustomerBody,
): Promise<{ resendAfter: number; notice: string } | { error: string; resendAfter?: number }> => {
  const r = await AddCustomer({ ...customer, email_code: true });
  if (r.error) return { error: r.error };
  const wait = Number(r.data?.resend_after) || undefined;
  if (r.data?.error) return { error: r.data.error, resendAfter: wait };
  return { resendAfter: wait ?? RESEND_AFTER_SECONDS, notice: r.data?.detail ?? "We sent you a new code." };
};
